/* ---------------------------------------------------------- */
/* Prose component                                            */
/* ---------------------------------------------------------- */
/* Table of contents:                                         */
/* 1. Container                                               */
/* 2. Element spacing                                         */
/* 3. Line-length control                                     */
/* 4. Headings                                                */
/* 5. Paragraphs                                              */
/* 6. Lists                                                   */
/* 7. Inline elements                                         */
/* 8. Images                                                  */
/* 9. Block elements                                          */
/* ---------------------------------------------------------- */

/* ---------------------------------------------------------- */
/* 1. Container                                               */
/* ---------------------------------------------------------- */

.Prose {
  font-family: var(--brand-body-fontFamily);
  font-size: var(--brand-text-size-200);
  line-height: var(--brand-text-lineHeight-200);
  color: var(--brand-color-text-default);
}

.Prose--editorial {
  font-size: var(--brand-text-size-300);
  line-height: 1.7;
}

/* ---------------------------------------------------------- */
/* 2. Element spacing                                         */
/* ---------------------------------------------------------- */

.Prose * + * {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: auto;
  margin-block-start: var(--spacing, 1em);
}

.Prose--editorial * + * {
  --spacing: var(--base-size-40);
}

/* ---------------------------------------------------------- */
/* 3. Line-length control                                     */
/* ---------------------------------------------------------- */

.Prose--lineLength {
  max-width: var(--brand-Prose-lineLength);
}

/* ---------------------------------------------------------- */
/* 4. Headings                                                */
/* ---------------------------------------------------------- */

.Prose h1 {
  font-weight: var(--brand-heading-weight-700);
  font-size: var(--brand-text-size-700);
  line-height: var(--brand-text-lineHeight-700);
  font-family: var(--brand-heading-fontFamily);
  letter-spacing: var(--brand-text-letterSpacing-700);
}

.Prose--lineLength h1 {
  max-width: 22ch;
}

.Prose h2 {
  font-weight: var(--brand-heading-weight-600);
  font-size: var(--brand-text-size-600);
  line-height: var(--brand-text-lineHeight-600);
  font-family: var(--brand-heading-fontFamily);
  letter-spacing: var(--brand-text-letterSpacing-600);
}

.Prose--lineLength h2 {
  max-width: 26ch;
}

.Prose h3 {
  font-weight: var(--brand-heading-weight-400);
  font-size: var(--brand-text-size-400);
  line-height: var(--brand-heading-lineHeight-400);
  font-family: var(--brand-heading-fontFamily);
  letter-spacing: var(--brand-heading-letterSpacing-400);
}

.Prose--lineLength h1 {
  max-width: 32ch;
}

.Prose h4 {
  --spacing: var(--base-size-48);
  font-weight: var(--brand-heading-weight-400);
  font-size: var(--brand-text-size-300);
  line-height: var(--brand-text-lineHeight-300);
  font-family: var(--brand-heading-fontFamily);
  letter-spacing: var(--brand-heading-letterSpacing-300);
}

.Prose h5 {
  --spacing: var(--base-size-48);
  font-weight: var(--brand-text-subhead-weight-medium);
  font-size: var(--brand-text-subhead-size-medium);
  line-height: var(--brand-text-subhead-lineHeight-medium);
  font-family: var(--brand-heading-fontFamily);
  letter-spacing: var(--brand-heading-letterSpacing-200);
}

.Prose h6 {
  --spacing: var(--base-size-48);
  font-weight: var(--brand-text-subhead-weight-medium);
  font-size: var(--brand-text-subhead-size-medium);
  line-height: var(--brand-text-subhead-lineHeight-medium);
  font-family: var(--brand-heading-fontFamily);
  letter-spacing: var(--brand-heading-letterSpacing-200);
}

.Prose :is(h1, h2, h3) {
  --spacing: var(--base-size-64);
}

.Prose :is(h1, h2, h3) + * {
  --spacing: var(--base-size-24);
}

/* ---------------------------------------------------------- */
/* 5. Paragraphs                                              */
/* ---------------------------------------------------------- */

.Prose p {
  --spacing: var(--base-size-24);
}

.Prose--editorial p {
  --spacing: var(--base-size-40);
}

/* ---------------------------------------------------------- */
/* 6. Lists                                                   */
/* ---------------------------------------------------------- */

.Prose ul,
.Prose ol {
  --spacing: var(--base-size-16);
  display: flex;
  flex-direction: column;
  gap: var(--base-size-8);
  padding: 0;
  margin-inline-start: var(--base-size-24);
  line-height: var(--brand-text-lineHeight-300);
}

.Prose--editorial ul,
.Prose--editorial ol {
  --spacing: var(--base-size-48);
  gap: var(--base-size-16);
  line-height: 1.7;
}

.Prose ul {
  list-style-type: image;
  list-style-image: var(--brand-Prose-unorderedList-imageUrl);
}

.Prose li {
  --spacing: 0;
}

/* ---------------------------------------------------------- */
/* 7. Inline elements                                         */
/* ---------------------------------------------------------- */

.Prose strong,
.Prose b {
  font-weight: var(
    --brand-text-weight-heavy
  ) !important; /* Overrides Primer product styles in dotcom. Should compute to 550 */
}

.Prose em {
  font-variation-settings: 'ital' 10;
  font-synthesis: none;
}

.Prose a {
  color: var(--brand-InlineLink-color-rest);
  text-decoration: underline;
  text-decoration-color: var(--brand-InlineLink-color-rest);
  text-decoration-thickness: var(--brand-borderWidth-thin);
  text-underline-position: under;
  transition: var(--brand-InlineLink-transition-timing) text-decoration;
  position: relative;
}

.Prose a:hover {
  text-decoration-thickness: var(--brand-borderWidth-thick);
}

.Prose a:active::after {
  border-color: var(--brand-InlineLink-color-pressed);
}

.Prose code {
  font-family: var(--brand-fontStack-monospace);
  font-size: var(--brand-text-size-100);
  white-space: break-spaces;
  padding: var(--base-size-4) var(--base-size-8);
  border-radius: var(--brand-borderRadius-small);
  background-color: var(--brand-Prose-code-bgColor);
}

.Prose pre {
  padding: var(--base-size-16);
  border-radius: var(--brand-borderRadius-medium);
  background-color: var(--brand-Prose-code-bgColor);
}

.Prose pre code {
  padding: 0;
}

/* ---------------------------------------------------------- */
/* 8. Images                                                  */
/* ---------------------------------------------------------- */

.Prose img {
  --spacing: var(--brand-Prose-img-spacing);
  display: block;
  max-width: 100%;
  height: auto;
  margin-left: auto;
  margin-right: auto;
  margin-block-end: var(--spacing);
}

.Prose--editorial figure img {
  margin-block-end: 0;
  border-radius: var(--brand-borderRadius-medium);
  display: block;
}

/* ---------------------------------------------------------- */
/* 9. Block elements                                          */
/* ---------------------------------------------------------- */

.Prose blockquote {
  --spacing: var(--brand-Prose-blockquote-spacing);
  position: relative;
  font-size: var(--brand-text-size-400);
  line-height: var(--brand-text-lineHeight-400);
  padding-top: var(--base-size-48);
  padding-bottom: var(--base-size-8);
  margin-bottom: var(--spacing);
}

.Prose--default blockquote {
  padding-left: var(--base-size-40);
}

.Prose--editorial blockquote {
  font-size: var(--brand-text-size-500);
  line-height: var(--brand-text-lineHeight-500);
  font-weight: var(--brand-text-weight-500);
  padding-top: var(--base-size-64);
}

@media screen and (min-width: 87.5rem) {
  .Prose--editorial blockquote {
    margin-block: var(--base-size-40);
  }
}

.Prose blockquote::before {
  content: '“';
  font-size: var(--base-size-48);
  line-height: var(--base-size-64);
  font-weight: var(--base-text-weight-extrabold);
  position: absolute;
  top: 0;
}

.Prose--editorial blockquote::before {
  color: var(--brand-color-accent-primary);
  font-size: var(--base-size-64);
  line-height: var(--base-size-64);
}

.Prose--default blockquote::after {
  content: '';
  display: block;
  position: absolute;
  width: var(--brand-borderWidth-thick);
  height: 100%;
  top: 0;
  left: 0;
  background-color: var(--base-color-scale-indigo-5);
  background-image: linear-gradient(180deg, var(--base-color-scale-blue-5), var(--base-color-scale-purple-5));
}

.Prose figure figcaption {
  --spacing: var(--base-size-24);
  font-family: var(--brand-fontStack-monospace);
  font-size: var(--brand-text-size-100);
  line-height: var(--brand-text-lineHeight-100);
  color: var(--brand-color-text-muted);
}

.Prose--editorial figcaption {
  font-size: var(--brand-text-size-100);
  line-height: var(--brand-text-lineHeight-100);
  font-weight: var(--brand-text-weight-100);
}

.Prose--editorial hr {
  --spacing: var(--base-size-64);
  margin-block-start: var(--spacing);
  margin-block-end: var(--spacing);
  border: 1px solid var(--brand-color-border-muted);
}

.Prose video,
.Prose iframe[src*='youtube.com'],
.Prose iframe[src*='youtu.be'],
.Prose iframe[src*='youtube-nocookie.com'] {
  --spacing: var(--brand-Prose-img-spacing);

  border-radius: var(--brand-borderRadius-medium);
  display: block;
  height: auto;
  margin-left: auto;
  margin-right: auto;
  margin-block-end: var(--spacing);
}

.Prose video {
  max-width: 100%;
}

.Prose iframe[src*='youtube.com'],
.Prose iframe[src*='youtu.be'],
.Prose iframe[src*='youtube-nocookie.com'] {
  width: 100%;
  aspect-ratio: 16 / 9;
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .Prose a,
  .Prose a::before,
  .Prose a::after,
  .Prose a * {
    transition: none !important;
  }
}

/* Tables */

.Prose table {
  --spacing: var(--base-size-32);
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: var(--brand-text-size-200);
  line-height: var(--brand-text-lineHeight-200);
}

.Prose table caption {
  text-align: left;
  padding-block-end: var(--base-size-24);
  font-size: var(--brand-text-size-100);
  font-weight: var(--brand-text-weight-100);
  line-height: var(--brand-text-lineHeight-100);
  color: var(--brand-color-text-muted);
}

.Prose table th,
.Prose table td {
  padding-block: var(--base-size-16);
  padding-inline: var(--base-size-12);
  text-align: left;
  vertical-align: top;
  border-bottom: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
}

.Prose table th:first-child,
.Prose table td:first-child,
.Prose table th:last-child,
.Prose table td:last-child {
  padding-inline: 0;
}

.Prose table th {
  font-size: var(--brand-text-size-100);
  font-weight: var(--brand-text-weight-400);
  line-height: var(--brand-text-lineHeight-100);
  color: var(--brand-color-text-muted);
}

.Prose table thead th {
  border-bottom: var(--brand-borderWidth-thick) solid var(--brand-color-border-default);
}

.Prose table tbody tr:last-child td {
  border-bottom: none;
}

.Prose--editorial table {
  --spacing: var(--base-size-48);
}

.Prose td > ul:first-child,
.Prose td > ol:first-child {
  margin-block-start: 0;
}

@media screen and (min-width: 48rem) {
  .Prose table th,
  .Prose table td {
    padding: var(--base-size-16) var(--base-size-8);
  }

  .Prose table th:not(:first-child):not(:last-child),
  .Prose table td:not(:first-child):not(:last-child) {
    padding-inline: var(--base-size-16);
  }

  .Prose table th:first-child,
  .Prose table td:first-child {
    padding-inline-start: 0;
  }

  .Prose table th:last-child,
  .Prose table td:last-child {
    padding-inline-end: 0;
  }

  .Prose table tbody tr:hover {
    background-color: var(--brand-color-canvas-subtle);
  }
}

@media screen and (max-width: 47.99rem) {
  .Prose table:has(td[data-label]) {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    background: none;
    box-shadow: none;
    font-size: var(--brand-text-size-200); /* inherit from Prose */
  }

  /* Visually hidden only */
  .Prose table:has(td[data-label]) thead {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }

  .Prose table:has(td[data-label]) tbody,
  .Prose table:has(td[data-label]) tr {
    display: block;
    width: 100%;
  }

  .Prose table:has(td[data-label]) th:not(:first-child):not(:last-child),
  .Prose table:has(td[data-label]) td:not(:first-child):not(:last-child) {
    padding-inline: 0;
  }

  .Prose table:has(td[data-label]) tr {
    border-block-start: var(--brand-borderWidth-thicker) solid var(--brand-color-border-muted);
    padding-block-start: var(--base-size-20);
    margin-block-end: var(--spacing);
  }

  .Prose td[data-label] {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0;
    margin: 0 0 var(--base-size-16, 16px) 0;
    border: none;
    align-items: flex-start;
  }

  .Prose td[data-label]:not(:last-child) {
    border-block-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    padding-block-end: var(--base-size-20);
  }

  .Prose td[data-label]::before {
    content: attr(data-label);
    color: var(--brand-color-text-muted);
    margin-block-end: var(--base-size-4);
    text-align: left;
    font-weight: var(--brand-text-weight-400);
  }

  .Prose table:has(td[data-label]) td:not([data-label]) {
    display: block;
    width: 100%;
    padding: 0;
    margin-block-end: var(--base-size-16);
    border: none;
  }

  .Prose table:has(td[data-label]) td:not([data-label]):not(:last-child) {
    border-block-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    padding-block-end: var(--base-size-20);
  }

  .Prose td[data-label]:last-child {
    margin-block-end: 0;
  }
}
